import { AxiosInstance, AxiosProgressEvent, AxiosRequestConfig, AxiosResponse } from 'axios';
import { err, ok } from 'neverthrow';

import { Asset } from '@rover/types/src/ImagePicker';

import { getAxiosInstance } from './axios';
import HttpResponse from './HttpResponse';
import { snakeify } from './object-utilities';
import parseAxiosError from './parseAxiosError';
import { HttpResult } from './types';

/**
 * !!! PLEASE NOTE !!!
 *
 * You probably don't need to use these interfaces. Our React apps are moving towards
 * using our auto generated react-query client instead of axios or HTTP directly.
 * Read more at https://roverdotcom.atlassian.net/wiki/spaces/TECH/pages/2887648670/RSDK+OpenAPI+Client
 *
 * This module will remain for the rare cases where we do want direct axios usage.
 *
 * Callouts:
 * - These interfaces are not officially supported by the Client Platform Team beyond our own usages in RSDK
 * - There is no automatic reporting of errors like in other HTTP interfaces at Rover
 * - These interfaces return a `neverthrow` Result type that you may not be used to
 */

export async function get<SnakeCaseAPIResponseType>(
  url: string
): Promise<HttpResult<SnakeCaseAPIResponseType>> {
  const ai = getAxiosInstance();
  try {
    const res = await ai.get(url);
    return ok(new HttpResponse<SnakeCaseAPIResponseType>(res));
  } catch (e) {
    // axios throws on non-200 responses
    const parsedError = parseAxiosError(e);
    return err(parsedError);
  }
}

export async function post<SnakeCaseAPIResponseType>(
  url: string,
  body: string | Record<string, string>,
  config?: AxiosRequestConfig,
  axiosInstance?: AxiosInstance // supports a single odd case in modules/Logging/Logger.ts
): Promise<HttpResult<SnakeCaseAPIResponseType>> {
  const ai = axiosInstance || getAxiosInstance();
  try {
    const res = await ai.post<unknown, AxiosResponse<SnakeCaseAPIResponseType>>(
      url,
      typeof body === 'string' ? body : snakeify(body),
      config
    );
    return ok(new HttpResponse<SnakeCaseAPIResponseType>(res));
  } catch (e) {
    // axios throws on non-200 responses
    const parsedError = parseAxiosError(e);
    return err(parsedError);
  }
}

export interface PutListeners {
  onUploadProgress?: (event: AxiosProgressEvent) => void;
}

export type PutData = Asset;

export async function put<SnakeCaseAPIResponseType>(
  url: string,
  data: PutData,
  listeners?: PutListeners
): Promise<HttpResult<SnakeCaseAPIResponseType>> {
  const ai = getAxiosInstance();
  try {
    // handle the (presumably rare) case of an Asset without a type
    if (!data.type) {
      throw new Error('Cannot PUT data without a type param');
    }
    const res = await ai.put<PutData, AxiosResponse<SnakeCaseAPIResponseType>>(url, data, {
      headers: {
        'Content-Type': data.type,
        'Content-Disposition': 'inline',
      },
      ...listeners,
    });
    return ok(new HttpResponse<SnakeCaseAPIResponseType>(res));
  } catch (e) {
    // axios throws on non-200 responses
    const parsedError = parseAxiosError(e);
    return err(parsedError);
  }
}
